import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';

const Products = () => {
  const categories = [
    {
      title: 'Spices',
      image: 'https://images.unsplash.com/photo-1581600140682-d4e68c8cde32?w=800',
      description: 'Whole, ground, and blends with consistent aroma, color, and specifications',
      link: '/products/spices',
      details: [
        'Export-ready lots with standardized specs',
        'Bulk and private-label packaging options',
        'Consistent quality across shipments',
        'QA gates and COA capture for every lot'
      ]
    },
    {
      title: 'Edible Oils & Fats',
      image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=800',
      description: 'Refined and blended supply with spec-based sourcing',
      link: '/products/edible-oils',
      details: [
        'Multiple grades and specifications available',
        'Bulk tanker and container shipments',
        'Documentation support for all destinations',
        'Flexible packaging formats'
      ]
    },
    {
      title: 'Sugar',
      image: 'https://images.unsplash.com/photo-1499636136210-6f4ee915583e?w=800',
      description: 'Bulk supply for distributors and industrial buyers',
      link: '/products/sugar',
      details: [
        'Consistent specification and quality',
        'Multiple grades available (refined, raw)',
        'Large volume capacity',
        'Reliable lead times and documentation'
      ]
    },
    {
      title: 'Cereals, Pulses & Grains',
      image: 'https://images.unsplash.com/photo-1598048851887-0263d4f43e73?w=800',
      description: 'Procurement with consistency plan for reliable supply',
      link: '/products/grains',
      details: [
        'Wide variety of grains and pulses',
        'Quality-checked lots with traceability',
        'Bulk supply for food processors',
        'Seasonal planning and forward contracts'
      ]
    },
    {
      title: 'Fruits & Vegetables',
      image: 'https://images.unsplash.com/photo-1579113800032-c38bd7635818?w=800',
      description: 'Fresh and preserved options with cold-chain support',
      link: '/products/produce',
      details: [
        'Fresh produce with cold-chain logistics',
        'Preserved/processed fruits and vegetables',
        'Quality sorting and grading',
        'Temperature-controlled storage and transport'
      ]
    },
    {
      title: 'Dehydrated Aromatics',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/cd882a8944ead0a49d53ba82cabd72a907092f0b14e407f2fa4381bad34b77e7.png',
      description: 'Premium dehydrated onion, garlic, ginger, and specialty powders',
      link: '/products/dehydrated-aromatics',
      details: [
        'Export-quality dehydrated products',
        'Multiple formats: flakes, powder, granules',
        'Strict quality control and testing',
        'Custom specifications available'
      ]
    },
    {
      title: 'Dry Fruits',
      image: 'https://static.prod-images.emergentagent.com/jobs/2a82a0ed-6235-40ba-a9f3-10baa6fa26b5/images/ee20ff0401cdf86b7989f0ceed5e5fc26a265f99fa000464050ad1a0453ec047.png',
      description: 'Premium cashews, almonds, and assorted dry fruits for retail, HoReCa, and manufacturing',
      link: '/products/dry-fruits',
      details: [
        'Whole kernels and graded pieces',
        'Multiple grades: Standard Export & Premium EU/USA-Ready',
        'Batch COA and lot-coded traceability',
        'Destination-aligned specifications (UAE, UK-EU, USA, Africa)'
      ]
    },
    {
      title: 'ORIVANA Wellness',
      image: 'https://static.prod-images.emergentagent.com/jobs/eb4529a2-4ca6-41dd-a194-af190196c3ae/images/24de089d2fe994bf370732b0f01b55bff434c81030e30910a7c40d9be8baca51.png',
      description: 'Premium Ayurvedic supplements, herbal teas, and wellness products under the ORIVANA brand',
      link: '/products/wellness',
      details: [
        'USDA Organic & GMP certified products',
        'Supplements: Ashwagandha, Shilajit, Triphala, and more',
        'Herbal teas: Tulsi blends, green teas, wellness infusions',
        'Shop by health concern: Immunity, Digestion, Stress, Energy'
      ]
    },
    {
      title: 'ORIVANA Fresh',
      image: 'https://images.pexels.com/photos/1458694/pexels-photo-1458694.jpeg?auto=compress&cs=tinysrgb&w=800',
      description: 'Premium fresh produce for wholesalers and retailers - Grapes, Mangoes, Onions & Vegetables',
      link: '/products/orivana-fresh',
      featured: true,
      details: [
        'Direct farm sourcing with complete traceability',
        'Grapes: Sonaka, Thompson, Black, Crimson varieties',
        'Mangoes: GI-tagged Alphonso, Kesar, Dasheri & more',
        'Export-grade onions and seasonal vegetables'
      ]
    }
  ];

  const useCases = [
    'Importers looking for consistent supply',
    'Distributors building reliable lanes',
    'Private label and own-brand partners',
    'Industrial buyers and food processors',
    'Retail procurement teams',
    'HORECA suppliers'
  ];

  return (
    <div className="w-full">
      <SEO 
        title="Products - Premium Indian Exports | SPANXPORT"
        description="Explore SPANXPORT's premium Indian exports: Spices, Dry Fruits, Edible Oils, Grains, Sugar, Fresh Produce, and Wellness Products. Built for bulk trade and repeat export lanes."
        keywords="Indian spices export, dry fruits wholesale, edible oils, basmati rice, ORIVANA fresh produce, wellness products, bulk trade"
        url="/products"
      />
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">Products</h1>
            <p className="text-xl text-slate-700">
              Built for bulk trade, distributors, and repeat export lanes.
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto space-y-16">
            {categories.map((category, index) => (
              <div key={index} className={`grid grid-cols-1 md:grid-cols-2 gap-12 items-center`}>
                <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                  {category.link ? (
                    <Link to={category.link}>
                      <img
                        src={category.image}
                        alt={category.title}
                        className="rounded-lg shadow-lg w-full hover:shadow-2xl transition-shadow cursor-pointer"
                      />
                    </Link>
                  ) : (
                    <img
                      src={category.image}
                      alt={category.title}
                      className="rounded-lg shadow-lg w-full"
                    />
                  )}
                </div>
                <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                  {category.link ? (
                    <Link to={category.link}>
                      <h2 className="text-3xl font-bold mb-4 hover:text-[#F47D20] transition-colors cursor-pointer">{category.title}</h2>
                    </Link>
                  ) : (
                    <h2 className="text-3xl font-bold mb-4">{category.title}</h2>
                  )}
                  <p className="text-lg text-slate-700 mb-6">{category.description}</p>
                  <ul className="space-y-3 mb-6">
                    {category.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="text-[#263776] mr-2 font-bold">✓</span>
                        <span className="text-slate-700">{detail}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="bg-[#263776] text-white hover:bg-[#1e2a5e]">
                    <Link to={category.link || '/contact'}>
                      {category.link ? `Explore ${category.title}` : `Get a Quote for ${category.title}`}
                    </Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Who Uses Our Products</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {useCases.map((useCase, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                    <p className="text-lg text-slate-700">{useCase}</p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Documentation */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Quality & Documentation Support</h2>
            <p className="text-lg text-slate-700 mb-8">
              Every product category is backed by our EximStack system, ensuring traceability, QA gates, and export-ready documentation.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6">
                <h3 className="font-semibold mb-2">Traceability</h3>
                <p className="text-sm text-slate-600">Batch dossiers with origin and handling evidence</p>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold mb-2">QA Gates</h3>
                <p className="text-sm text-slate-600">Sampling, COA capture, and spec verification</p>
              </Card>
              <Card className="p-6">
                <h3 className="font-semibold mb-2">Export Pack</h3>
                <p className="text-sm text-slate-600">Complete documentation with destination checks</p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Source Your Commodity?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Share your spec, quantity, and destination—we'll provide a structured quote with lead times and documentation checklist.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Request Specs & Pricing</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white text-white bg-white/10 hover:bg-white/20">
              <Link to="/eximstack">Learn About EximStack</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
